import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import imgTable from '../../assets/images/Table.png';
import imgView from '../../assets/images/View.png';
import imgProcedure from '../../assets/images/Procedure.png';
import imgFunction from '../../assets/images/Function.png';
import imgSequence from '../../assets/images/Sequence.png';
import imgStage from '../../assets/images/Stage.png';
import imgFileFormat from '../../assets/images/File Format.png';
import imgPipe from '../../assets/images/Pipe.png';
import imgStream from '../../assets/images/Stream.png';
import imgTask from '../../assets/images/Task.png';
import imgPolicy from '../../assets/images/Policy.png';
import imgExternalFunction from '../../assets/images/External Function.png';
import imgAlert from '../../assets/images/Alert.png';
import imgContainer from '../../assets/images/container.png';
import imgTag from '../../assets/images/Tag.png';

// Maps API object_type (uppercase, underscores) → image asset
const OBJECT_TYPE_ICONS = {
    TABLE: imgTable,
    VIEW: imgView,
    PROCEDURE: imgProcedure,
    FUNCTION: imgFunction,
    SEQUENCE: imgSequence,
    STAGE: imgStage,
    'FILE FORMAT': imgFileFormat,
    PIPE: imgPipe,
    STREAM: imgStream,
    TASK: imgTask,
    POLICY: imgPolicy,
    'EXTERNAL FUNCTION': imgExternalFunction,
    ALERT: imgAlert,
    TAG: imgTag,
};

const OBJECT_TYPE_ORDER = Object.keys(OBJECT_TYPE_ICONS);

const getObjectIcon = (type) => OBJECT_TYPE_ICONS[type?.toUpperCase()] || imgContainer;

// ── Object Section (Created / Dropped / Altered) ──────────────────────────
// API shape: [{ object_type: "TABLE", object_names: ["USERS", "CARS"] }, ...]
// Flatten into one row per name.
const ObjectSection = ({ count, title, items, color }) => {
    const [expanded, setExpanded] = useState(false);

    const colorMap = {
        green: { title: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' },
        red: { title: '#dc2626', bg: '#fff1f2', border: '#fecdd3' },
        orange: { title: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    };
    const c = colorMap[color] ?? { title: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' };

    // Flatten: [{ object_type, object_names[] }] → [{ type, name }]
    const rows = (items || []).flatMap((entry) => ({
        type: entry.object_type || "Object",
        name: (entry.object_names || [])
            .map((name) => name.split('(')[0])
            .join(', ')
    })
    );

    // Sort rows by OBJECT_TYPE_ORDER sequence; unknown types go to the end
    const sortedRows = [...rows].sort((a, b) => {
        const indexA = OBJECT_TYPE_ORDER.indexOf(a.type?.toUpperCase());
        const indexB = OBJECT_TYPE_ORDER.indexOf(b.type?.toUpperCase());
        const orderA = indexA === -1 ? Infinity : indexA;
        const orderB = indexB === -1 ? Infinity : indexB;
        return orderA - orderB;
    });

    if (sortedRows.length === 0) return null;
    const isEmpty = sortedRows.length === 0;

    return (
        <div className="sr-object-section" style={{ borderColor: c.border }}>
            <button
                className="sr-object-header"
                style={{
                    background: expanded ? c.bg : 'white',
                    cursor: isEmpty ? 'default' : 'pointer',
                }}
                onClick={isEmpty ? undefined : () => setExpanded(v => !v)}
            >
                <span className="sr-object-title" style={{ color: c.title }}>
                    {title} ({count})
                </span>
                {!isEmpty && (expanded
                    ? <ChevronDown style={{ width: '1.25vw', height: '1.25vw', color: c.title }} />
                    : <ChevronRight style={{ width: '1.25vw', height: '1.25vw', color: c.title }} />
                )}
            </button>

            {expanded && (
                <div className="sr-object-body">
                    {sortedRows.map((row, i) => (
                        <div key={i} className="sr-object-row">
                            <img src={getObjectIcon(row.type)} alt={row.type} className="sr-object-icon" />
                            <span className="sr-object-type">
                                {row.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                            </span>
                            <span className="sr-object-name">{row.name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SchemaPairAccordion = ({ pair, defaultOpen }) => {

    // summary counts come from pair.summary
    const summary = pair.summary || {};
    const created = pair.created ?? [];
    const dropped = pair.dropped ?? [];
    const altered = pair.altered ?? [];

    const createdCount = created.flatMap(e => e.object_names || []).length;
    const droppedCount = dropped.flatMap(e => e.object_names || []).length;
    const alteredCount = altered.flatMap(e => e.object_names || []).length;

    const hasAnyObjects = createdCount > 0 || droppedCount > 0 || alteredCount > 0;

    const [expanded, setExpanded] = useState((defaultOpen && hasAnyObjects) || false);

    return (
        <div className={`sr-pair-card ${expanded ? 'sr-pair-card--open' : ''}`}>
            <button
                className="sr-pair-header"
                style={{ cursor: hasAnyObjects ? 'pointer' : 'default' }}
                onClick={hasAnyObjects ? () => setExpanded(v => !v) : undefined}
            >
                <div className="sr-pair-schemas">
                    <span className={`sr-pair-schema-name ${expanded ? 'sr-pair-schema-name--active' : ''}`}>
                        {pair.source_schema}
                    </span>
                    <span className="summary-arrow-icon">
                        <ChevronRight style={{ width: '0.885vw', height: '0.885vw', color: '#94a3b8', flexShrink: 0 }} />
                    </span>
                    <span className={`sr-pair-schema-name ${expanded ? 'sr-pair-schema-name--active' : ''}`}>
                        {pair.target_schema}
                    </span>
                </div>

                <div className="sr-pair-badges">
                    <span className={`sr-badge ${createdCount > 0 ? 'sr-badge--green' : 'sr-badge--gray'}`}>
                        {summary.created ?? createdCount} Created
                    </span>
                    <span className={`sr-badge ${droppedCount > 0 ? 'sr-badge--red' : 'sr-badge--gray'}`}>
                        {summary.dropped ?? droppedCount} Dropped
                    </span>
                    <span className={`sr-badge ${alteredCount > 0 ? 'sr-badge--orange' : 'sr-badge--gray'}`}>
                        {summary.altered ?? alteredCount} Altered
                    </span>
                    <div style={{ width: '1.25vw' }}>
                        {hasAnyObjects && (
                            expanded
                                ? <ChevronDown style={{ width: '1.25vw', height: '1.25vw', color: '#2563eb' }} />
                                : <ChevronRight style={{ width: '1.25vw', height: '1.25vw', color: '#6b7280' }} />
                        )}
                    </div>
                </div>
            </button>

            {expanded && (
                <div className="sr-pair-body">
                    <ObjectSection title="Objects Created" count={summary.created} items={created} color={createdCount > 0 ? 'green' : 'gray'} />
                    <ObjectSection title="Objects Dropped" count={summary.dropped} items={dropped} color={droppedCount > 0 ? 'red' : 'gray'} />
                    <ObjectSection title="Objects Altered" count={summary.altered} items={altered} color={alteredCount > 0 ? 'orange' : 'gray'} />
                </div>
            )}
        </div>
    );
};

export default SchemaPairAccordion;